import React, { useEffect, useState } from "react";
import {
  FaChartLine,
  FaMousePointer,
  FaDollarSign,
  FaEye,
  FaGlobe,
  FaUpload,
  FaTimes,
  FaUsers,
} from "react-icons/fa";
import Papa from "papaparse";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ApiHandler } from "../../helper/ApiHandler";
import { setUserDashboardData } from "../../redux/slices/userSlice";
import { toast } from "react-toastify";
import Loading from "../../components/Loading";
  
/* ================= NUMBER FIX ================= */

const parseApiNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return v;

  let s = String(v).trim();

  // remove $ and %
  s = s.replace(/[$%]/g, "");

  // remove commas
  s = s.replace(/,/g, "");

  const n = Number(s);
  return isNaN(n) ? 0 : n;
};


const formatInt = (value) => {
  const n = Math.floor(parseApiNumber(value));
  return isNaN(n) ? "0" : String(n);
};

const formatMoney = (value) => {
  const n = parseApiNumber(value);
  return isNaN(n) ? "0.00" : n.toFixed(2);
};

const calcPercent = (base, percent) =>
  (parseApiNumber(base) * parseApiNumber(percent)) / 100;

/* ================= COMPONENT ================= */

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userToken, userDashboard } = useSelector((s) => s.user);

  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [originalRows, setOriginalRows] = useState([]);
  const [csvMode, setCsvMode] = useState("v1");

  // ---- ADD3: manual date state ----
  const [manualDate, setManualDate] = useState("");

  /* ================= DASHBOARD API ================= */

  useEffect(() => {
    if (!userToken) return;

    setLoading(true);
    ApiHandler(
      "/get_admin_dashboard_data_new.php",
      "GET",
      null,
      userToken,
      dispatch,
      navigate
    )
      .then((res) => {
        if (res?.data?.status === "1") {
          dispatch(setUserDashboardData(res.data.data));
        }
      })
      .finally(() => setLoading(false));
  }, [userToken]);

  /* ================= DASHBOARD CARDS ================= */

  const cards = [
    {
      title: "Total Impressions",
      value: formatInt(userDashboard?.total_impressions),
      icon: <FaChartLine />,
      gradient: "bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 text-white",
    },
    {
      title: "Total Clicks",
      value: formatInt(userDashboard?.total_click),
      icon: <FaMousePointer />,
      gradient: "bg-gradient-to-r from-green-400 via-green-500 to-green-600 text-white",
    },
    {
      title: "Revenue",
      value: `$${formatMoney(userDashboard?.revenue)}`,
      icon: <FaDollarSign />,
      gradient: "bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 text-white",
    },
    {
      title: "Fill Rate",
      value: `${formatMoney(userDashboard?.fill_rate)}%`,
      icon: <FaChartLine />,
      gradient: "bg-gradient-to-r from-purple-400 via-purple-500 to-purple-600 text-white",
    },
    {
      title: "CTR",
      value: `${formatMoney(userDashboard?.ctr)}%`,
      icon: <FaMousePointer />,
      gradient: "bg-gradient-to-r from-red-400 via-red-500 to-red-600 text-white",
    },
    {
      title: "CPM",
      value: `$${formatMoney(userDashboard?.cpm)}`,
      icon: <FaDollarSign />,
      gradient: "bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600 text-white",
    },
    {
      title: "Viewability",
      value: `${formatMoney(userDashboard?.viewability)}%`,
      icon: <FaEye />,
      gradient: "bg-gradient-to-r from-indigo-400 via-indigo-500 to-indigo-600 text-white",
    },
    {
      title: "Viewable Impressions",
      value: formatInt(userDashboard?.viewable_imps),
      icon: <FaChartLine />,
      gradient: "bg-gradient-to-r from-pink-400 via-pink-500 to-pink-600 text-white",
    },
    {
      title: "Total Users",
      value: formatInt(userDashboard?.total_user),
      icon: <FaUsers />,
      gradient: "bg-gradient-to-r from-gray-400 via-gray-500 to-gray-600 text-white",
    },
    {
      title: "Blocked Users",
      value: formatInt(userDashboard?.block_user),
      icon: <FaUsers />,
      gradient: "bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 text-white",
    },
    {
      title: "Unblocked Users",
      value: formatInt(userDashboard?.unblock_user),
      icon: <FaUsers />,
      gradient: "bg-gradient-to-r from-green-400 via-green-500 to-green-600 text-white",
    },
    {
      title: "Total Domains",
      value: formatInt(userDashboard?.total_domain),
      icon: <FaGlobe />,
      gradient: "bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 text-white",
    },
  ];

  /* ================= CSV UPLOAD ================= */

  const handleCSVChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append("csv_file", file);

    try {
      const uploadUrl =
        csvMode === "v3"
          ? "https://api.myad360.com/csv/upload_multiple_revenue3.php"
          : csvMode === "v2"
            ? "https://api.myad360.com/csv/upload_multiple_revenue2.php"
            : "https://api.myad360.com/csv/upload_multiple_revenue.php";

      const res = await fetch(uploadUrl, {
        method: "POST",
        body: fd,
      });

      const json = await res.json();
      const rows = Array.isArray(json?.data) ? json.data : [];

      if (!rows.length) {
        toast.error("No valid data found");
        return;
      }

      setCsvData(rows);

      const headers = Object.keys(rows[0]).filter((h) => h !== "app_msg");

      setCsvHeaders(["app_status", ...headers.filter((h) => h !== "app_status")]);

      setOriginalRows(
        rows.map((r) => ({
          revenue: parseApiNumber(r.revenue),
          impressions: parseApiNumber(r.impressions),
          clicks: parseApiNumber(r.clicks),
        }))
      );

      setSelectedRows({});
      toast.success("CSV loaded successfully");
    } catch {
      toast.error("CSV upload failed");
    }
  };

  const handlePercentChange = (rowIndex, percent) => {
    setCsvData((prev) =>
      prev.map((row, i) => {
        if (i !== rowIndex) return row;
        const base = originalRows[i];

        return {
          ...row,
          revenue: calcPercent(base.revenue, percent).toFixed(2),
          impressions: Math.floor(calcPercent(base.impressions, percent)),
          clicks: Math.floor(calcPercent(base.clicks, percent)),
        };
      })
    );
  };

  const toggleRow = (i) =>
    setSelectedRows((p) => ({ ...p, [i]: !p[i] }));

  /* ================= SELECT ALL ================= */

  // Only active rows can be selected
  const activeRowIndices = csvData
    .map((row, i) => (Number(row.app_status) === 1 ? i : null))
    .filter((i) => i !== null);

  const allActiveSelected =
    activeRowIndices.length > 0 &&
    activeRowIndices.every((i) => !!selectedRows[i]);

  const someActiveSelected =
    activeRowIndices.some((i) => !!selectedRows[i]) && !allActiveSelected;

  const toggleSelectAll = () => {
    if (allActiveSelected) {
      // Deselect all active rows
      const updated = { ...selectedRows };
      activeRowIndices.forEach((i) => {
        updated[i] = false;
      });
      setSelectedRows(updated);
    } else {
      // Select all active rows
      const updated = { ...selectedRows };
      activeRowIndices.forEach((i) => {
        updated[i] = true;
      });
      setSelectedRows(updated);
    }
  };

  /* ================= DATE FORMATTER (shared) ================= */

  const formatDateForApi = (raw) => {
    raw = String(raw).trim();

    let d, m, y;

    // MM/DD/YYYY or M/D/YYYY
    if (raw.includes("/")) {
      const p = raw.split("/");
      if (p[2]?.length === 4) {
        m = p[0];
        d = p[1];
        y = p[2];
      } else {
        d = p[0];
        m = p[1];
        y = p[2];
      }
    }

    // YYYY-MM-DD (HTML date input returns this format)
    else if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const p = raw.split("-");
      y = p[0];
      m = p[1];
      d = p[2];
    }

    // DD-MM-YYYY
    else if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const p = raw.split("-");
      d = p[0];
      m = p[1];
      y = p[2];
    }

    // DD MM YYYY
    else if (/^\d{2}\s\d{2}\s\d{4}$/.test(raw)) {
      const p = raw.split(" ");
      d = p[0];
      m = p[1];
      y = p[2];
    }

    d = d?.padStart(2, "0");
    m = m?.padStart(2, "0");

    return `${d}-${m}-${y}`;
  };

  /* ================= SUBMIT ================= */

  const submitSelected = async () => {
    // For v3: manual date is required
    if (csvMode === "v3" && !manualDate) {
      toast.error("Please enter a date before submitting");
      return;
    }

    const payload = csvData
      .filter((row, i) => selectedRows[i] && Number(row.app_status) === 1)
      .map((row) => {
        const rawDate =
          csvMode === "v3"
            ? manualDate
            : row.date || row.report_date || "02-02-2026";

        return {
          id: String(row.app_data?.id),
          user_id: String(row.app_data?.user_id),
          app_name: row.app_data?.app_name || row.app_name || "",
          date: formatDateForApi(rawDate),
          revenue: parseApiNumber(row.revenue),
          impressions: Math.floor(parseApiNumber(row.impressions)),
          clicks: Math.floor(parseApiNumber(row.clicks)),
          ctr: parseApiNumber(row.ctr),
          match_rate: parseApiNumber(row.match_rate),
          show_rate: parseApiNumber(row.show_rate),
          cpm: parseApiNumber(row.ecpm),
        };
      });

    if (!payload.length) {
      toast.error("Select at least one VALID row");
      return;
    }

    console.log(payload, "payload");

    try {
      const res = await fetch(
        "https://api.myad360.com/api/v1/FNV89uJ9sud/admin/add_revenue_multiple_from_csv.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            token: userToken,
          },
          body: JSON.stringify(payload),
        }
      );

      const json = await res.json();

      if (json?.status === "1" || json?.success) {
        toast.success("Revenue data submitted successfully");
        setOpenModal(false);
        setSelectedRows({});
        setManualDate("");
      } else {
        toast.error(json?.message || "Submission failed");
      }
    } catch {
      toast.error("API error while submitting data");
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      {/* ================= CARDS ================= */}
      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {cards.map((card, index) => (
          <div
            key={index}
            className={`p-6 rounded-lg shadow-md flex flex-col items-center justify-center ${card.gradient}`}
          >
            <div className="text-4xl mb-2">{card.icon}</div>
            <h3 className="text-lg font-semibold">{card.title}</h3>
            <p className="text-xl font-bold">{card.value}</p>
          </div>
        ))}
      </div>

      {/* ================= BUTTONS ================= */}
      <div className="flex justify-start items-center">
        {/* v1 */}
        <div className="p-4 text-right">
          <button
            onClick={() => {
              setCsvMode("v1");
              setOpenModal(true);
              setCsvData([]);
              setOriginalRows([]);
              setSelectedRows({});
              setManualDate("");
            }}
            className="bg-indigo-600 text-white px-4 py-2 rounded"
          >
            CSV All Data ADD
          </button>
        </div>

        {/* v2 */}
        <div className="p-4 text-right">
          <button
            onClick={() => {
              setCsvMode("v2");
              setOpenModal(true);
              setCsvData([]);
              setOriginalRows([]);
              setSelectedRows({});
              setManualDate("");
            }}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            CSV All Data ADD2
          </button>
        </div>

        {/* v3 */}
        <div className="p-4 text-right">
          <button
            onClick={() => {
              setCsvMode("v3");
              setOpenModal(true);
              setCsvData([]);
              setOriginalRows([]);
              setSelectedRows({});
              setManualDate("");
            }}
            className="bg-orange-500 text-white px-4 py-2 rounded"
          >
            CSV All Data ADD3
          </button>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      {openModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-[999999] flex justify-center items-start pt-10">
          <div className="bg-white w-[92%] max-w-7xl rounded shadow p-4 max-h-[90vh] overflow-y-auto">

            {/* Header */}
            <div className="flex justify-between border-b pb-2">
              <h2 className="font-semibold">
                Upload Revenue CSV
                {csvMode === "v3" && (
                  <span className="ml-2 text-orange-500 text-sm font-normal">(ADD3)</span>
                )}
              </h2>
              <FaTimes
                className="cursor-pointer"
                onClick={() => {
                  setOpenModal(false);
                  setManualDate("");
                }}
              />
            </div>

            {/* v3: Manual Date Input */}
            {csvMode === "v3" && (
              <div className="my-3 flex items-center gap-3">
                <label className="font-medium text-sm text-gray-700 whitespace-nowrap">
                  Select Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={manualDate}
                  onChange={(e) => setManualDate(e.target.value)}
                  className="border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                {manualDate && (
                  <span className="text-xs text-gray-500">
                    Will submit as:{" "}
                    <strong>{formatDateForApi(manualDate)}</strong>
                  </span>
                )}
              </div>
            )}

            {/* File Input */}
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVChange}
              className="my-3"
            />

            {/* CSV TABLE */}
            {csvData.length > 0 && (
              <div className="max-h-[500px] overflow-auto border mt-3">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      {/* ====== SELECT ALL CHECKBOX ====== */}
                      <th className="border p-2 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <input
                            type="checkbox"
                            title="Select / Deselect all active rows"
                            checked={allActiveSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = someActiveSelected;
                            }}
                            onChange={toggleSelectAll}
                            className="cursor-pointer w-4 h-4"
                          />
                          <span className="text-xs font-normal text-gray-500 leading-none">
                            All
                          </span>
                        </div>
                      </th>
                      {/* ================================= */}
                      <th className="border p-2">Status</th>
                      <th className="border p-2">App</th>
                      <th className="border p-2">
                        {csvMode === "v3" ? "Manual Date" : "Date"}
                      </th>
                      <th className="border p-2">Revenue ($)</th>
                      <th className="border p-2">eCPM ($)</th>
                      <th className="border p-2">Match Rate (%)</th>
                      <th className="border p-2">Show Rate (%)</th>
                      <th className="border p-2">Impressions</th>
                      <th className="border p-2">Clicks</th>
                      <th className="border p-2">CTR (%)</th>
                      <th className="border p-2">%</th>
                    </tr>
                  </thead>

                  <tbody>
                    {csvData.map((row, i) => {
                      const active = Number(row.app_status) === 1;
                      return (
                        <tr key={i} className={active ? "" : "bg-gray-200 opacity-60"}>
                          <td className="border p-2 text-center">
                            <input
                              type="checkbox"
                              disabled={!active}
                              checked={!!selectedRows[i]}
                              onChange={() => toggleRow(i)}
                            />
                          </td>

                          <td className="border p-2 text-center">
                            {active ? (
                              <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                                Active
                              </span>
                            ) : (
                              <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">
                                Inactive
                              </span>
                            )}
                          </td>

                          <td className="border p-2">
                            {row.app_data?.app_name || row.app || row.app_name || "Unknown App"}
                          </td>

                          <td className="border p-2 text-center">
                            {csvMode === "v3"
                              ? manualDate
                                ? formatDateForApi(manualDate)
                                : <span className="text-red-400 text-xs">No date set</span>
                              : row.date || row.report_date}
                          </td>

                          <td className="border p-2 text-right">
                            ${formatMoney(row.revenue)}
                          </td>

                          <td className="border p-2 text-right">
                            ${formatMoney(row.ecpm)}
                          </td>

                          <td className="border p-2 text-right">
                            {formatMoney(row.match_rate)}%
                          </td>

                          <td className="border p-2 text-right">
                            {formatMoney(row.show_rate)}%
                          </td>

                          <td className="border p-2 text-right">
                            {formatInt(row.impressions)}
                          </td>

                          <td className="border p-2 text-center">
                            {csvMode === "v2" || csvMode === "v3" ? (
                              <input
                                type="number"
                                min="0"
                                className="border p-1 w-20 text-center"
                                value={formatInt(row.clicks)}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setCsvData((prev) =>
                                    prev.map((r, idx) =>
                                      idx === i ? { ...r, clicks: v } : r
                                    )
                                  );
                                }}
                              />
                            ) : (
                              formatInt(row.clicks)
                            )}
                          </td>

                          <td className="border p-2 text-right">
                            {formatMoney(row.ctr)}%
                          </td>

                          <td className="border p-2 text-center">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              className="border p-1 w-16 text-center"
                              placeholder="100"
                              onChange={(e) =>
                                handlePercentChange(i, e.target.value || 100)
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  {/* TOTAL */}
                  <tfoot className="bg-yellow-100 font-bold">
                    <tr>
                      <td colSpan="4" className="border p-2 text-right">TOTAL</td>

                      <td className="border p-2 text-right">
                        ${csvData.reduce((t, r) => t + parseApiNumber(r.revenue), 0).toFixed(2)}
                      </td>

                      <td className="border p-2 text-right">
                        ${(
                          (csvData.reduce((t, r) => t + parseApiNumber(r.revenue), 0) /
                            csvData.reduce((t, r) => t + parseApiNumber(r.impressions), 0)) *
                          1000
                        ).toFixed(2)}
                      </td>

                      <td className="border p-2 text-right">
                        {(csvData.reduce((t, r) => t + parseApiNumber(r.match_rate), 0) / csvData.length).toFixed(2)}%
                      </td>

                      <td className="border p-2 text-right">
                        {(csvData.reduce((t, r) => t + parseApiNumber(r.show_rate), 0) / csvData.length).toFixed(2)}%
                      </td>

                      <td className="border p-2 text-right">
                        {csvData.reduce((t, r) => t + parseApiNumber(r.impressions), 0)}
                      </td>

                      <td className="border p-2 text-right">
                        {csvData.reduce((t, r) => t + parseApiNumber(r.clicks), 0)}
                      </td>

                      <td className="border p-2 text-right">
                        {(
                          (csvData.reduce((t, r) => t + parseApiNumber(r.clicks), 0) /
                            csvData.reduce((t, r) => t + parseApiNumber(r.impressions), 0)) *
                          100
                        ).toFixed(2)}%
                      </td>

                      <td className="border p-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* SUBMIT */}
            <div className="flex justify-end mt-4">
              <button
                onClick={submitSelected}
                className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2"
              >
                <FaUpload /> Submit Selected
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboard;